import { type ChangeEvent, Fragment, type MouseEvent, type ReactElement, useRef, useState } from "react"

import {
  Button,
  Checkbox,
  InputText,
  Menu,
  MenuItem,
  RadioButton,
  Skeleton,
  Typography,
} from "@synapse-analytics/synapse-ui"
import type { MenuProps } from "@synapse-analytics/synapse-ui/build/components/Menu/Menu"
import type { TooltipWrapperProps } from "@synapse-analytics/synapse-ui/build/components/Tooltip/ToolTip"

import { CustomArrowAdornment } from "../CustomArrowAdornment"

import styles from "./GroupedSelectMenu.module.scss"

export type MenuOption = { id: string; value: string | number; label: string }
type Group = MenuOption & { options: MenuOption[] }

type RadioBtnProps =
  | {
      enableMultiGroupSelect: false
      inputValue?: never
      onRadioBtnSelect?: (ev: MouseEvent<HTMLInputElement>) => void
    }
  | {
      enableMultiGroupSelect: true
      inputValue: string
      onRadioBtnSelect?: never
    }

type ClearBtnProps =
  | {
      enableClearBtn: true
      onClearAll?: () => void
    }
  | { enableClearBtn: false; onClearAll?: never }

type GroupSelectMenuProps = {
  groups: Group[]
  initialInputValue?: Group["value"]
  initialSelectedOptions?: MenuOption[]
  inputPlaceHolder?: string
  isLoading?: boolean
  title?: string
  menuTitle?: string
  labelTooltipProps?: Omit<TooltipWrapperProps, "children"> | undefined
  onCheckboxChange?: (group: MenuOption, item: MenuOption, ev: ChangeEvent<HTMLInputElement>) => void
  inputMenuProps?: MenuProps
} & RadioBtnProps &
  ClearBtnProps

/**
 * A dropdown menu component to select one or more items from a grouped list of options.
 *
 * @param {Group[]} groups - The grouped list of options.
 * @param {string} [initialInputValue] - The initial value of the input field.
 * @param {MenuOption[]} [initialSelectedOptions] - The initial selected items.
 * @param {string} [inputPlaceHolder] - The placeholder text for the input field.
 * @param {boolean} [isLoading] - Whether the component is currently loading.
 * @param {string} [title] - The title of the component.
 * @param {string} [menuTitle] - The title of the menu.
 * @param {TooltipWrapperProps} [labelTooltipProps] - The tooltip props for the label.
 * @param {boolean} enableMultiGroupSelect - Whether to allow selecting multiple groups.
 * @param {boolean} enableClearBtn - Whether to display a clear all button.
 * @param {(group: MenuOption, item: MenuOption, ev: ChangeEvent<HTMLInputElement>) => void} [onCheckboxChange] - The handler for when a checkbox is changed.
 * @param {(ev: MouseEvent<HTMLInputElement>) => void} [onRadioBtnSelect] - The handler for when a radio button is selected.
 * @param {() => void} [onClearAll] - The handler for when the clear all button is clicked.
 *
 * @returns {ReactElement} The GroupSelectMenu component.
 */
export function GroupSelectMenu(props: GroupSelectMenuProps): ReactElement {
  const {
    menuTitle,
    inputValue,
    initialInputValue,
    initialSelectedOptions,
    groups,
    inputPlaceHolder,
    isLoading,
    title,
    labelTooltipProps,
    enableMultiGroupSelect,
    enableClearBtn,
    inputMenuProps,
    onCheckboxChange,
    onRadioBtnSelect,
    onClearAll,
  } = props

  const inputRef = useRef<HTMLInputElement>(null)

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [selectedMenuValue, setSelectedMenuValue] = useState(initialInputValue ?? "")
  const [selectedItems, setSelectedItems] = useState<MenuOption[]>(initialSelectedOptions ?? [])

  const inputClickHandler = (): void => {
    if (!!anchorEl) {
      setAnchorEl(null)
    } else {
      setAnchorEl(inputRef.current)
      inputRef?.current?.focus()
    }
  }

  const radioButtonClickHandler = (ev: MouseEvent<HTMLInputElement>): void => {
    const value = ev.currentTarget.value
    setSelectedMenuValue(value)

    setSelectedItems([])

    onRadioBtnSelect?.(ev)
  }

  const checkboxChangeHandler = (group: MenuOption, item: MenuOption, ev: ChangeEvent<HTMLInputElement>): void => {
    const target = ev.target

    if (target.checked) {
      setSelectedItems((prev) => [...prev, { id: item.id, value: target.value, label: item.label }])
    } else {
      setSelectedItems((prev) => prev.filter((selectedItem) => selectedItem.id !== item.id))
    }

    onCheckboxChange?.(group, item, ev)
  }

  const clearAllClickHandler = (): void => {
    setSelectedItems([])
    onClearAll?.()
  }

  return (
    <div>
      {isLoading ? (
        <Skeleton />
      ) : (
        <div>
          <InputText
            value={enableMultiGroupSelect ? inputValue : selectedMenuValue}
            placeholder={inputPlaceHolder}
            isSelect
            endAdornment={<CustomArrowAdornment isMenuOpen={!!anchorEl} handleAdornmentClick={inputClickHandler} />}
            handleInputClick={inputClickHandler}
            anchoringRef={inputRef}
            hideDescription
            fullWidth
            menuProps={inputMenuProps}
            label={title}
            labelTooltipProps={labelTooltipProps}
          />

          <Menu
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={() => setAnchorEl(null)}
            menuMaxContent
            className={styles.menuContainer}
          >
            {groups.length === 0 ? (
              <MenuItem className={styles.menuItem}>No Items found</MenuItem>
            ) : (
              <Fragment>
                {menuTitle && (
                  <li>
                    <Typography variant="span-regular" gutterBottom>
                      {menuTitle}
                    </Typography>
                  </li>
                )}

                {groups.map(({ id: groupId, label: groupLabel, value: groupValue, options }) => (
                  <MenuItem className={styles.menuItem} key={groupId}>
                    <div className={styles.radioBtn}>
                      {enableMultiGroupSelect ? (
                        <Typography variant="label-bold" variantColor={2}>
                          {groupLabel}
                        </Typography>
                      ) : (
                        <RadioButton
                          label={groupLabel}
                          name="group"
                          value={groupValue}
                          onClick={radioButtonClickHandler}
                          checked={selectedMenuValue === groupValue}
                        />
                      )}
                    </div>

                    <div className={styles.checkboxesContainer}>
                      {options.map(({ id, label, value }) => (
                        <Checkbox
                          key={id}
                          label={label}
                          value={value}
                          name={id}
                          disabled={selectedMenuValue !== groupValue && !enableMultiGroupSelect}
                          checked={!!selectedItems.find((item) => item.id === id)}
                          onChange={checkboxChangeHandler.bind(
                            null,
                            { id: groupId, label: groupLabel, value: groupValue },
                            { id, label, value },
                          )}
                        />
                      ))}
                    </div>
                  </MenuItem>
                ))}

                {enableClearBtn && (
                  <li className={styles.actionBtn}>
                    <Button onClick={clearAllClickHandler}>Clear all</Button>
                  </li>
                )}
              </Fragment>
            )}
          </Menu>
        </div>
      )}
    </div>
  )
}
